import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectIsLoggingOut } from "../../reduxSlices/globalFlagsSlice";
import Warning from "../Warning/Warning";

const setCookie = (name: string, value: string, days: number) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = name + "=; Max-Age=-99999999; path=/;";
};

const MESSAGE_TYPES = {
  QUERY_LEADER: "QUERY_LEADER",
  ANNOUNCE_LEADER: "ANNOUNCE_LEADER",
  TAKEOVER: "TAKEOVER",
};

interface OneTabEnforcerProps {
  children: ReactNode;
}

const OneTabEnforcer = ({ children }: OneTabEnforcerProps) => {
  const isLoggingOut = useSelector(selectIsLoggingOut);
  const [rol, setRol] = useState<"INDETERMINADO" | "PRINCIPAL" | "DUPLICADO">("INDETERMINADO");
  const rolRef = useRef(rol);
  const [fallbackReady, setFallbackReady] = useState(false);
  rolRef.current = rol;
  const channelRef = useRef<BroadcastChannel | null>(null);
  const tabId = useRef(Date.now().toString());
  const didInit = useRef(false);
  const cookieName = "primaryTabId_cookie";

  // Función unificada para establecer este tab como principal
  const establecerComoPrincipal = useCallback((regenerarTabId: boolean = false, notificar: boolean = true) => {
    if (isLoggingOut) {
      console.log("Logout en progreso, no tomando control");
      return;
    }

    if (regenerarTabId) {
      tabId.current = Date.now().toString();
      console.log(`Nuevo Tab ID generado: ${tabId.current}`);
    }

    setCookie(cookieName, tabId.current, 1);
    setRol("PRINCIPAL");

    if (notificar) {
      channelRef.current?.postMessage({ type: MESSAGE_TYPES.TAKEOVER });
    }
  }, [isLoggingOut]);

  // Alias para mantener compatibilidad
  const tomarControlComoPrincipal = useCallback(() => {
    establecerComoPrincipal(false, true);
  }, [establecerComoPrincipal]);

  // Función unificada para verificar si este tab es el líder
  const verificarSiEsLider = useCallback((): boolean => {
    const leaderIdInCookie = getCookie(cookieName);
    return leaderIdInCookie === tabId.current;
  }, []);

  // Función unificada para marcarse como duplicado si no es el líder
  const verificarYMarcarDuplicado = useCallback(() => {
    if (isLoggingOut) {
      return;
    }
    if (!verificarSiEsLider()) {
      setRol("DUPLICADO");
    }
  }, [isLoggingOut, verificarSiEsLider]);

  // Función auxiliar para consultar si hay otros tabs activos
  const consultarOtrosTabs = useCallback((onNoHayOtrosTabs: () => void) => {
    if (!channelRef.current) {
      // Si no hay canal, asumir que no hay otros tabs y ejecutar callback
      onNoHayOtrosTabs();
      return;
    }

    const hayOtrosTabsRef = { value: false };
    let queryTimeout: NodeJS.Timeout | null = null;
    let cleanupTimeout: NodeJS.Timeout | null = null;

    const handleQueryResponse = (event: MessageEvent) => {
      const { type } = event.data;
      
      // Si recibimos respuesta de otro tab, significa que hay tabs activos
      if (type === MESSAGE_TYPES.ANNOUNCE_LEADER || type === MESSAGE_TYPES.TAKEOVER) {
        hayOtrosTabsRef.value = true;
        if (queryTimeout) {
          clearTimeout(queryTimeout);
          queryTimeout = null;
        }
        if (cleanupTimeout) {
          clearTimeout(cleanupTimeout);
          cleanupTimeout = null;
        }
        console.log("Se detectaron otros tabs activos, no tomando control");
        
        // Remover el listener temporal
        channelRef.current?.removeEventListener("message", handleQueryResponse);
        
        // Verificar y marcar como duplicado si corresponde
        verificarYMarcarDuplicado();
      }
    };
    
    // Agregar listener temporal
    channelRef.current.addEventListener("message", handleQueryResponse);
    
    // Enviar consulta
    channelRef.current.postMessage({ type: MESSAGE_TYPES.QUERY_LEADER });
    
    // Timeout: si no hay respuesta en 500ms, asumimos que no hay otros tabs activos
    queryTimeout = setTimeout(() => {
      queryTimeout = null;
      if (cleanupTimeout) {
        clearTimeout(cleanupTimeout);
        cleanupTimeout = null;
      }
      if (!hayOtrosTabsRef.value && !isLoggingOut) {
        console.log("No se detectaron otros tabs activos, procediendo...");
        channelRef.current?.removeEventListener("message", handleQueryResponse);
        onNoHayOtrosTabs();
      }
    }, 500);
    
    // Limpiar el listener después de un tiempo mayor al timeout
    cleanupTimeout = setTimeout(() => {
      channelRef.current?.removeEventListener("message", handleQueryResponse);
      if (queryTimeout) {
        clearTimeout(queryTimeout);
      }
    }, 600);
  }, [isLoggingOut, verificarYMarcarDuplicado]);

  const verificarYRegenerarTabId = useCallback(() => {
    if (isLoggingOut) {
      return;
    }
    
    const liderExistenteId = getCookie(cookieName);
    
    // Si existe un tab ID pero no coincide con el actual
    if (liderExistenteId && liderExistenteId !== tabId.current) {
      console.log("Verificación de Tab ID: Tab ID no coincide, verificando si hay otros tabs...");
      console.log(`Tab ID existente: ${liderExistenteId}, Tab ID actual: ${tabId.current}`);
      
      consultarOtrosTabs(() => {
        if (isLoggingOut) return;
        console.log("Tomando control como principal...");
        establecerComoPrincipal(true, true);
      });
    } else if (!liderExistenteId) {
      // Si no hay tab ID, consultar primero si hay otros tabs antes de tomar control
      console.log("Verificación de Tab ID: No hay Tab ID, verificando si hay otros tabs...");
      
      consultarOtrosTabs(() => {
        if (isLoggingOut) return;
        console.log("Estableciendo como principal...");
        establecerComoPrincipal(false, false);
      });
    }
  }, [isLoggingOut, consultarOtrosTabs, establecerComoPrincipal]);

  // Función unificada para limpiar cookie al cerrar el tab
  const limpiarCookieSiEsLider = useCallback(() => {
    if (isLoggingOut) {
      return;
    }
    if (verificarSiEsLider()) {
      deleteCookie(cookieName);
    }
  }, [isLoggingOut, verificarSiEsLider]);

  useEffect(() => {
    if (didInit.current || typeof document === "undefined") {
      return;
    }
    didInit.current = true;
    const channelName = "ditella-app-channel";
    let takeoverTimeout: NodeJS.Timeout | null = null;
    channelRef.current = new BroadcastChannel(channelName);

    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data;

      if (type === MESSAGE_TYPES.TAKEOVER || type === MESSAGE_TYPES.ANNOUNCE_LEADER) {
        if (takeoverTimeout) clearTimeout(takeoverTimeout);
        verificarYMarcarDuplicado();
      }

      if (type === MESSAGE_TYPES.QUERY_LEADER && rolRef.current === "PRINCIPAL") {
        channelRef.current?.postMessage({ type: MESSAGE_TYPES.ANNOUNCE_LEADER });
      }
    };

    channelRef.current.onmessage = handleMessage;

    const liderExistenteId = getCookie(cookieName);

    // Verificar y regenerar Tab ID siempre al montarse (en cualquier página)
    verificarYRegenerarTabId();

    // Si después de la verificación no hay líder o estamos en logout, tomar control
    if (!liderExistenteId || isLoggingOut) {
      if (!isLoggingOut) {
        // Si la verificación no estableció el rol, tomar control
        if (rolRef.current !== "PRINCIPAL") {
          tomarControlComoPrincipal();
        }
      }
    } else {
      // Si hay un líder existente, consultar antes de tomar control
      takeoverTimeout = setTimeout(() => {
        if (!isLoggingOut) {
          // Solo tomar control si después de la verificación no somos principales
          if (rolRef.current !== "PRINCIPAL") {
            tomarControlComoPrincipal();
          }
        }
      }, 500);
      channelRef.current.postMessage({ type: MESSAGE_TYPES.QUERY_LEADER });
    }

    const handleBeforeUnload = () => {
      limpiarCookieSiEsLider();
    };

    const handleUnload = () => {
      limpiarCookieSiEsLider();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
      if (takeoverTimeout) clearTimeout(takeoverTimeout);
      channelRef.current?.close();
    };
  }, [tomarControlComoPrincipal, verificarYRegenerarTabId, verificarSiEsLider, limpiarCookieSiEsLider, isLoggingOut]);

  useEffect(() => {
    if (rol !== "PRINCIPAL") {
      return;
    }

    const verifyLeadership = () => {
      if (isLoggingOut) {
        return;
      }
      verificarYMarcarDuplicado();
    };

    const verificationInterval = setInterval(verifyLeadership, 3000);

    // Función unificada para cuando la app vuelve al foco
    const handleAppFocus = () => {
      if (isLoggingOut) {
        return;
      }
      verificarYRegenerarTabId();
      verifyLeadership();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleAppFocus();
      }
    };

    const handleFocus = () => {
      handleAppFocus();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(verificationInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [rol, isLoggingOut, verificarYRegenerarTabId]);

  // Efecto para verificar Tab ID cuando la app se monta o cambia de página
  useEffect(() => {
    if (!isLoggingOut) {
      // Verificar Tab ID al montarse o cambiar de página
      verificarYRegenerarTabId();
    }
  }, [verificarYRegenerarTabId, isLoggingOut]);

  // Evitar parpadeo del fallback: esperar a que la negociación inicial termine
  // antes de mostrar el fallback (cubre la ventana de 500ms de BroadcastChannel)
  useEffect(() => {
    if (rol === "DUPLICADO") {
      const timer = setTimeout(() => setFallbackReady(true), 700);
      return () => clearTimeout(timer);
    }
    setFallbackReady(false);
  }, [rol]);

  if (isLoggingOut) {
    return <>{children}</>;
  }

  if (rol === "DUPLICADO") {
    if (!fallbackReady) return <>{children}</>;
    return <Warning takeControl={tomarControlComoPrincipal} />;
  }

  if (rol === "PRINCIPAL") {
    return <>{children}</>;
  }

  return null;
};

export default OneTabEnforcer;
